import { useQuery } from '@apollo/client/react';
import type { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import type { BillingSubscriptionProductDetailsFragment } from '@graphql/fragments/billing-subscription/product.graphql';
import { BillingSubscriptionPricesDocument } from '@graphql/queries/billing-subscription/price.graphql';
import { useMemo } from 'react';

import { CURRENCY_CODE_SYMBOL_MAP, getCurrencyCodeByCountry } from './currency-map';

interface UsePrices {
  /**
   * Blinq billing-subscription prices.
   */
  prices: BillingSubscriptionPriceDetailsFragment[];
  /**
   * Blinq billing-subscription products.
   */
  products: BillingSubscriptionProductDetailsFragment[];
  /**
   * The current user's currency, as a display symbol.
   */
  currency: string;
  /**
   * The ISO code behind `currency`. Exposed so a consumer narrowing prices by
   * currency and a consumer rendering the symbol cannot disagree about which one
   * they mean.
   */
  currencyCode: string;
  loading: boolean;
}

/**
 * Plans any surface may sell without asking.
 *
 * Pro is deliberately absent. It is current in the catalog so the referral plans
 * screen can sell it, but every other surface predates it and selects on workspace
 * type — under which Pro is indistinguishable from Premium. Withholding it here
 * makes that safe by default: a caller that never heard of Pro cannot show it, and
 * a new caller has to opt in rather than remember to opt out.
 */
const DEFAULT_SELLABLE_PLANS = ['premium', 'business'] as const;

interface UsePricesOptions {
  /** Plans to include. Defaults to everything but Pro — see `DEFAULT_SELLABLE_PLANS`. */
  plans?: readonly string[];
}

function usePrices({ plans = DEFAULT_SELLABLE_PLANS }: UsePricesOptions = {}): UsePrices {
  const { data, loading } = useQuery(BillingSubscriptionPricesDocument);
  const allowedPlans = plans.join(',');

  const prices = useMemo(() => {
    const allowed = allowedPlans.split(',');

    return (
      (data?.billingSubscriptionPrices ?? [])
        .filter((price) => price.product?.provider === 'stripe')
        .filter((price) => price.product?.is_current)
        // A product with no plan is left alone: the column is required in the
        // database, so this only covers fixtures that predate the field.
        .filter((price) => !price.product?.plan || allowed.includes(price.product.plan))
        .filter(
          (price): price is BillingSubscriptionPriceDetailsFragment => price.is_current === true,
        )
    );
  }, [data?.billingSubscriptionPrices, allowedPlans]);

  const products = useMemo(() => {
    return prices
      .map((price) => price.product)
      .filter((product): product is BillingSubscriptionProductDetailsFragment => !!product);
  }, [prices]);

  const currencyCode = useMemo(() => {
    return getCurrencyCodeByCountry({
      countryCode: 'US',
      defaultCurrencyCode: 'USD',
      whitelist: ['EUR', 'USD', 'AUD', 'CAD', 'GBP'],
    });
  }, []);

  const currency = useMemo(() => CURRENCY_CODE_SYMBOL_MAP[currencyCode], [currencyCode]);

  return {
    prices,
    products,
    currency,
    currencyCode,
    loading,
  };
}

export { usePrices };
